import { MutableRefObject, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { LocationsSkeleton } from '@virginexperiencedays/components-v2/src/pages/product/Locations/LocationsSkeleton';
// Hooks
import { Location } from '@virginexperiencedays/products';
import { GoogleEmbedMap } from './GoogleEmbedMap';

// Lazy loaded components
const GoogleMapComponent = dynamic(() => import('./GoogleMapComponent'), {
  loading: () => <LocationsSkeleton />,
});
const GoogleStaticImage = dynamic(() => import('./GoogleStaticImage'), {
  loading: () => <LocationsSkeleton />,
});

const generateMarkersString = (locations: Location[]) => {
  const markers = locations.map((location, index) => {
    if (index === 0) {
      return encodeURI(`markers=${location.lat},${location.lng}`);
    }
    return encodeURI(`&markers=${location.lat},${location.lng}`);
  });
  return markers.join('');
};

const transformRegionsIntoLocationsArray = (
  regions: { [key: string]: Location[] },
  activeRegion: string
) => {
  const locations = [];
  if (activeRegion) {
    regions[activeRegion].forEach((location) => locations.push(location));
  } else {
    Object.keys(regions).forEach((region) =>
      regions[region].forEach((location) => locations.push(location))
    );
  }
  return locations;
};

const LocationsMap = ({
  activeRegion,
  regions,
  GMAP_PUBLIC_API_KEY,
  isLocationsVisible,
}: {
  regions: { [key: string]: Location[] };
  activeRegion: string;
  isLocationsVisible: boolean;
  GMAP_PUBLIC_API_KEY: string;
}) => {
  const mapContainerRef: MutableRefObject<HTMLDivElement> = useRef();
  const [width, setWidth] = useState<number>();
  const [isStatic, setIsStatic] = useState(true);

  const locationsArray = transformRegionsIntoLocationsArray(regions, activeRegion);
  const markersString = generateMarkersString(locationsArray);

  // 400px in designs
  const containerHeight = 400;
  const staticImageUrl = `https://maps.googleapis.com/maps/api/staticmap?size=${
    width > 940 ? 940 : width
  }x${containerHeight}&${markersString}&key=${GMAP_PUBLIC_API_KEY}`;

  // Set width of map container
  useEffect(() => {
    if (mapContainerRef.current && isLocationsVisible) {
      setWidth(mapContainerRef.current.clientWidth);
    }
    window.addEventListener('resize', () => {
      if (mapContainerRef.current && isLocationsVisible) {
        setWidth(mapContainerRef.current.clientWidth);
      }
      return () =>
        window.removeEventListener('resize', () => {
          if (mapContainerRef.current && isLocationsVisible) {
            setWidth(mapContainerRef.current.clientWidth);
          }
        });
    });
  }, [mapContainerRef, isLocationsVisible]);

  // Placeholder for SEO
  if (!isLocationsVisible) {
    return (
      <noscript>
        <img src={staticImageUrl} decoding="async" loading="lazy" alt="google-maps-image" />;
      </noscript>
    );
  }

  // Single location, use free Google Embed API
  if (locationsArray.length < 2) {
    return (
      <div ref={mapContainerRef} className="h-[400px] w-full flex-1">
        <GoogleEmbedMap
          location={locationsArray[0]}
          API_KEY={GMAP_PUBLIC_API_KEY}
          width={width}
          height={containerHeight}
        />
      </div>
    );
  }

  return (
    <div ref={mapContainerRef} className="h-[400px] w-full flex-1">
      {isStatic ? (
        <GoogleStaticImage
          height={containerHeight}
          onClick={() => setIsStatic(false)}
          url={staticImageUrl}
        />
      ) : (
        <GoogleMapComponent
          width={width}
          height={containerHeight}
          markers={locationsArray}
          GMAP_PUBLIC_API_KEY={GMAP_PUBLIC_API_KEY}
        />
      )}
    </div>
  );
};

export default LocationsMap;
